<script lang="ts">
  import { onMount } from 'svelte';
  import { books } from '../stores/books';
  import { getBookStorage } from '../lib/storage';
  import BookUpload from './BookUpload.svelte';
  import OPDSBrowser from './OPDSBrowser.svelte';
  import type { Book } from '../../../../packages/storage-manager/src/index';
  import {
    exportToTxt,
    exportToPdf,
    exportToEpub,
    exportToFb2Zip,
    downloadBlob
  } from '../lib/book-exporter';

  let bookList: Book[] = [];
  let searchQuery = '';
  let loading = true;
  let openMenuBookId: string | null = null;
  let activeTab: 'library' | 'opds' = 'library';

  books.subscribe(value => {
    bookList = value;
  });

  onMount(async () => {
    try {
      const storage = getBookStorage();
      const loadedBooks = await storage.getAllBooks();
      books.set(loadedBooks);
    } catch (error) {
      console.error('Failed to load books:', error);
    } finally {
      loading = false;
    }

    // Close menu when clicking outside
    const handleClickOutside = () => {
      if (openMenuBookId) {
        closeMenu();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  async function handleDeleteBook(bookId: string, event: Event) {
    event.stopPropagation();
    closeMenu();

    if (!confirm('Are you sure you want to delete this book?')) {
      return;
    }

    try {
      const storage = getBookStorage();
      await storage.deleteBook(bookId);
      const updatedBooks = await storage.getAllBooks();
      books.set(updatedBooks);
    } catch (error) {
      console.error('Failed to delete book:', error);
    }
  }

  function formatDate(date: Date): string {
    return new Date(date).toLocaleDateString();
  }

  function handleBookClick(book: Book) {
    // Navigate using svelte5-router (History API)
    window.history.pushState({}, '', `/reader/${book.id}`);
    window.dispatchEvent(new Event('pushState'));
  }

  function toggleMenu(bookId: string, event: Event) {
    event.stopPropagation();
    openMenuBookId = openMenuBookId === bookId ? null : bookId;
  }

  function closeMenu() {
    openMenuBookId = null;
  }

  async function handleDownloadMd(book: Book, event: Event) {
    event.stopPropagation();
    closeMenu();

    try {
      const blob = new Blob([book.content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${book.title}.md`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download MD:', error);
    }
  }

  async function handleDownloadMdZip(book: Book, event: Event) {
    event.stopPropagation();
    closeMenu();

    try {
      // Dynamic import to avoid SSR issues
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      // Add README.md
      zip.file('README.md', book.content);

      // If book has bundle data with assets, add them
      if (book.bundle && book.bundle.assets) {
        for (const [path, asset] of Object.entries(book.bundle.assets)) {
          try {
            // Skip if no data
            if (!asset.data) {
              console.warn(`Skipping asset ${asset.path}: no data`);
              continue;
            }

            // Convert asset data to appropriate format for JSZip
            let data: ArrayBuffer | Uint8Array;

            if (typeof asset.data === 'string') {
              // Base64 string - decode to Uint8Array
              try {
                const binaryString = atob(asset.data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }
                data = bytes;
              } catch (e) {
                console.warn(`Failed to decode base64 for ${asset.path}:`, e);
                continue;
              }
            } else if (asset.data instanceof ArrayBuffer) {
              // Already ArrayBuffer
              data = asset.data;
            } else if (asset.data instanceof Uint8Array) {
              // Already Uint8Array
              data = asset.data;
            } else if (typeof asset.data === 'object' && 'buffer' in asset.data) {
              // Could be a typed array view
              data = new Uint8Array(asset.data.buffer);
            } else {
              console.warn(`Unsupported data type for ${asset.path}:`, typeof asset.data);
              continue;
            }

            zip.file(asset.path, data, { binary: true });
          } catch (assetError) {
            console.warn(`Failed to add asset ${asset.path}:`, assetError);
            // Continue with other assets even if one fails
          }
        }
      }

      // Generate ZIP
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${book.title}.md.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download MD.zip:', error);
      alert('Failed to download book. Some assets may be corrupted.');
    }
  }

  async function handleExportFormat(book: Book, format: string, event: Event) {
    event.stopPropagation();
    closeMenu();

    try {
      let blob: Blob;
      let extension: string;

      switch (format) {
        case 'txt':
          blob = await exportToTxt(book);
          extension = 'txt';
          break;
        case 'pdf':
          blob = await exportToPdf(book);
          extension = 'pdf';
          break;
        case 'epub':
          blob = await exportToEpub(book);
          extension = 'epub';
          break;
        case 'fb2.zip':
          blob = await exportToFb2Zip(book);
          extension = 'fb2.zip';
          break;
        default:
          throw new Error(`Unknown format: ${format}`);
      }

      downloadBlob(blob, `${book.title}.${extension}`);
    } catch (error) {
      console.error(`Failed to export to ${format}:`, error);
      alert(`Failed to export book to ${format} format.`);
    }
  }

  $: filteredBooks = searchQuery
    ? bookList.filter(b =>
        b.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : bookList;
</script>

<div class="library">
  <header class="mb-8">
    <h1 class="text-4xl font-bold mb-2">Legere</h1>
    <p class="text-gray-600 dark:text-gray-400">Your Personal Markdown Book Reader</p>
  </header>

  <!-- Tabs -->
  <div class="tabs mb-6">
    <button
      class="tab"
      class:active={activeTab === 'library'}
      on:click={() => activeTab = 'library'}
    >
      My Library
    </button>
    <button
      class="tab"
      class:active={activeTab === 'opds'}
      on:click={() => activeTab = 'opds'}
    >
      OPDS Catalogs
    </button>
  </div>

  {#if activeTab === 'library'}
    <BookUpload />

    <div class="search-bar mb-6 mt-8">
      <input
        type="text"
        bind:value={searchQuery}
        placeholder="Search books..."
        class="w-full px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
      />
    </div>

  {#if loading}
    <div class="text-center py-12 text-gray-500">
      <p>Loading library...</p>
    </div>
  {:else if filteredBooks.length === 0}
    <div class="empty-state text-center py-12 text-gray-500">
      {#if bookList.length === 0}
        <p class="text-xl mb-4">No books in your library</p>
        <p>Upload a .md, .md.zip, .fb2, or .fb2.zip file to get started</p>
      {:else}
        <p class="text-xl mb-4">No books found</p>
        <p>No matches found in your library of {bookList.length} book{bookList.length === 1 ? '' : 's'}</p>
        <button
          on:click={() => searchQuery = ''}
          class="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          Clear search
        </button>
      {/if}
    </div>
  {:else}
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {#each filteredBooks as book (book.id)}
        <div
          class="book-card p-4 border rounded-lg cursor-pointer hover:shadow-lg transition-shadow dark:border-gray-700 relative"
          on:click={() => handleBookClick(book)}
        >
          <!-- Actions Menu Button -->
          <div class="absolute bottom-2 right-2">
            <button
              on:click={(e) => toggleMenu(book.id, e)}
              class="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Actions"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </button>

            <!-- Dropdown Menu -->
            {#if openMenuBookId === book.id}
              <div class="absolute right-0 bottom-full mb-1 w-48 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg z-10 max-h-96 overflow-y-auto">
                <div class="py-1">
                  <div class="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Export</div>
                  <button
                    on:click={(e) => handleDownloadMd(book, e)}
                    class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Markdown (.md)
                  </button>
                  <button
                    on:click={(e) => handleDownloadMdZip(book, e)}
                    class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Bundle (.md.zip)
                  </button>
                  <button
                    on:click={(e) => handleExportFormat(book, 'txt', e)}
                    class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Plain Text (.txt)
                  </button>
                  <button
                    on:click={(e) => handleExportFormat(book, 'pdf', e)}
                    class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    PDF (.pdf)
                  </button>
                  <button
                    on:click={(e) => handleExportFormat(book, 'epub', e)}
                    class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    EPUB (.epub)
                  </button>
                  <button
                    on:click={(e) => handleExportFormat(book, 'fb2.zip', e)}
                    class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    FictionBook (.fb2.zip)
                  </button>
                </div>
                <div class="border-t dark:border-gray-700 py-1">
                  <button
                    on:click={(e) => handleDeleteBook(book.id, e)}
                    class="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            {/if}
          </div>

          <div class="flex items-start gap-2 mb-2">
            <h3 class="text-lg font-semibold flex-1">{book.title}</h3>
            {#if book.metadata.originalFormat === 'fb2' || book.metadata.originalFormat === 'fb2.zip'}
              <span class="px-2 py-0.5 text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-200 rounded-full whitespace-nowrap">
                from FB2
              </span>
            {/if}
          </div>
          {#if book.metadata.author}
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-2">
              by {book.metadata.author}
            </p>
          {/if}
          <div class="text-xs text-gray-500 dark:text-gray-500">
            <p>Added: {formatDate(book.createdAt)}</p>
            {#if book.lastReadAt}
              <p>Last read: {formatDate(book.lastReadAt)}</p>
            {/if}
          </div>
        </div>
      {/each}
    </div>
  {/if}
  {:else if activeTab === 'opds'}
    <OPDSBrowser />
  {/if}
</div>

<style>
  .tabs {
    display: flex;
    gap: 0.5rem;
    border-bottom: 2px solid #e5e7eb;
  }

  .tab {
    padding: 0.75rem 1.5rem;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    font-weight: 500;
    color: #6b7280;
    transition: all 0.2s;
    margin-bottom: -2px;
  }

  .tab:hover {
    color: #1f2937;
  }

  .tab.active {
    color: #2563eb;
    border-bottom-color: #2563eb;
  }
</style>
